import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { billingApi } from '../../api';
import { PageHeader, LoadingState, ErrorState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

const fmt = (n: number) => 'PKR ' + n.toLocaleString();

export function BillingPage() {
  const qc = useQueryClient();
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');

  const q = useQuery({ queryKey: ['billing', 'overview'], queryFn: () => billingApi.overview() });

  const subscribe = useMutation({
    mutationFn: (plan: string) => billingApi.subscribe(plan, cycle),
    onSuccess: () => {
      toast.success('Subscription updated');
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;

  const { subscription: sub, plans } = q.data as any;

  return (
    <div>
      <PageHeader title="Billing & subscription" subtitle="Your plan, trial status, and invoices" />

      {/* Current status */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Current plan</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', textTransform: 'capitalize' }}>{sub.plan}</div>
            <div style={{ marginTop: 4 }}>
              <StatusPill status={sub.status} />
              {sub.on_trial && (
                <span style={{ marginLeft: 8, fontSize: 13, color: '#b45309', fontWeight: 600 }}>
                  {sub.trial_days_left} day{sub.trial_days_left === 1 ? '' : 's'} left in trial
                </span>
              )}
              {!sub.on_trial && sub.status === 'active' && (
                <span style={{ marginLeft: 8, fontSize: 13, color: '#64748b' }}>
                  Renews in {sub.period_days_left} day{sub.period_days_left === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0d9488' }}>{fmt(sub.price)}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>per {sub.billing_cycle === 'yearly' ? 'year' : 'month'}</div>
          </div>
        </div>
      </div>

      {/* Cycle toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 0, marginBottom: 20 }}>
        <div style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
          {(['monthly', 'yearly'] as const).map((c) => (
            <button key={c} onClick={() => setCycle(c)}
              style={{ padding: '6px 18px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600,
                cursor: 'pointer', background: cycle === c ? '#fff' : 'transparent',
                color: cycle === c ? '#0f172a' : '#64748b',
                boxShadow: cycle === c ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
              {c === 'monthly' ? 'Monthly' : 'Yearly (save ~17%)'}
            </button>
          ))}
        </div>
      </div>

      {/* Plan catalog */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {plans.map((p: any) => {
          const price = cycle === 'yearly' ? p.yearly : p.monthly;
          const isCurrent = p.id === sub.plan;
          return (
            <div key={p.id} style={{ background: '#fff', border: isCurrent ? '2px solid #0d9488' : '1px solid #e2e8f0',
              borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{p.name}</div>
              <div style={{ margin: '8px 0 14px' }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{price === 0 ? 'Free' : fmt(price)}</span>
                {price > 0 && <span style={{ fontSize: 13, color: '#64748b' }}> /{cycle === 'yearly' ? 'yr' : 'mo'}</span>}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', flex: 1 }}>
                {p.features.map((f: string, i: number) => (
                  <li key={i} style={{ fontSize: 13, color: '#475569', padding: '4px 0', display: 'flex', gap: 6 }}>
                    <span style={{ color: '#0d9488', fontWeight: 700 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                disabled={isCurrent || subscribe.isPending}
                onClick={() => subscribe.mutate(p.id)}
                style={{ padding: '10px 14px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 14,
                  cursor: isCurrent ? 'default' : 'pointer',
                  background: isCurrent ? '#e2e8f0' : '#0d9488', color: isCurrent ? '#64748b' : '#fff',
                  opacity: subscribe.isPending ? 0.7 : 1 }}>
                {isCurrent ? 'Current plan' : (subscribe.isPending ? 'Working…' : 'Choose ' + p.name)}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    trialing: { bg: '#fef3c7', fg: '#b45309', label: 'Trialing' },
    active: { bg: '#dcfce7', fg: '#15803d', label: 'Active' },
    past_due: { bg: '#fee2e2', fg: '#b91c1c', label: 'Past due' },
    canceled: { bg: '#f1f5f9', fg: '#64748b', label: 'Canceled' },
    paused: { bg: '#f1f5f9', fg: '#64748b', label: 'Paused' },
  };
  const s = map[status] ?? { bg: '#f1f5f9', fg: '#64748b', label: status };
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12,
      fontWeight: 700, background: s.bg, color: s.fg }}>{s.label}</span>
  );
}
